/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Trans } from 'react-i18next';
import qs from 'query-string';
import { useAppNavigate } from 'hooks';

import { Modal, Button } from 'components/controls';
import { IWiseTableAction } from 'components/wise';
import * as Components from 'components/containers';

import { useCallCommand, useDelete } from 'plugins/api';
import { IDynamicAction } from 'plugins/api/types';
import { loadDataUrlParser, paramsToUrlParser } from 'plugins/util/parser';
import { DynamicPage } from 'plugins/pages';

import 'plugins/theme/components/TablePanel/TablePanel.scss';

// typ danych wejściowych
interface IProps {
  additionalAction: IDynamicAction | IWiseTableAction;
  item: any;
  refetchTableData: () => void;
  requestParams?: object;
}

type IComponentKeys = 'ClientForm' | 'Order';

const AdditionalActionCell: FC<IProps> = ({
  additionalAction,
  item,
  refetchTableData,
  requestParams
}) => {
  const { search } = useLocation();
  const navigate = useAppNavigate();

  const [open, setOpen] = useState(false);

  const [openModal, setOpenModal] = useState(false);

  // import komponentu po nazwie w definicji akcji
  const componentKey = additionalAction?.method_params?.component_name;
  const Component =
    typeof componentKey === 'string' ? Components[componentKey as IComponentKeys] : null;

  const endpointUrl = loadDataUrlParser(additionalAction.method_params?.command_url || '', {
    ...item,
    ...requestParams,
    ...qs.parse(search)
  });

  // parsowanie parametrów do urla
  const paramsUrl = paramsToUrlParser(additionalAction.method_params?.params_list || '', {
    ...qs.parse(search),
    ...requestParams,
    ...item
  });

  // wywołanie metody CallCommand
  const { mutate: callCommand } = useCallCommand(endpointUrl || '', {
    onSuccess: () => {
      refetchTableData();
    }
  });

  // wywołanie metody Delete
  const { mutate: deleteAction } = useDelete(endpointUrl || '', {
    onSuccess: () => {
      setOpen(false);
      refetchTableData();
    },
    onError: () => {
      setOpen(false);
    }
  });

  const handleActionClick = (additionalAction: IDynamicAction | IWiseTableAction) => {
    if (
      additionalAction.method === 'OpenModalComponent' ||
      additionalAction.method === 'OpenModalDynamicPage'
    ) {
      setOpenModal(true);
    }

    // obsłużenie akcji CallCommand
    if (additionalAction.method === 'CallCommand') {
      callCommand({});
    }

    // obsłużenie akcji Delete
    if (additionalAction.method === 'Delete') {
      setOpen(true);
    }

    if (additionalAction.method === 'Redirect') {
      additionalAction.method_params?.page_url &&
        navigate(additionalAction.method_params.page_url + (paramsUrl ? `?${paramsUrl}` : ''));
    }
  };

  const handleDelete = () => {
    deleteAction({});
  };

  const handleSuccess = () => {
    setOpen(false);

    if (additionalAction.refresh_data_mode === 'ONSUCCESS') {
      refetchTableData();
    }
  };

  const renderModalContent = () => {
    if (additionalAction.method === 'OpenModalComponent') {
      return (
        <Modal onClose={() => setOpen(false)} title="">
          {Component && (
            <Component onCancel={() => setOpen(false)} onSuccess={() => handleSuccess()} />
          )}
        </Modal>
      );
    }

    if (additionalAction.method === 'OpenModalDynamicPage') {
      const requestParams = additionalAction.method_params?.params_list
        ? qs.parse(
            paramsToUrlParser(additionalAction.method_params?.params_list, { ...qs.parse(search) })
          )
        : {};

      return (
        <Modal fullWidth onClose={() => setOpen(false)} title="">
          <DynamicPage
            pageSymbol={additionalAction.method_params?.dynamic_page || ''}
            componentParams={{
              fnCallback: () => setOpen(false),
              isModal: false,
              refetchFn: refetchTableData
            }}
            requestParams={requestParams}
          />
        </Modal>
      );
    }

    return null;
  };

  return (
    <>
      <div
        onClick={(e) => {
          e.stopPropagation();
          handleActionClick(additionalAction);
        }}>
        {additionalAction.label}
      </div>
      {open && (
        <Modal title={additionalAction.label} onClose={() => setOpen(false)}>
          {' '}
          <div className="confirmationModalActions">
            <Button onClick={() => setOpen(false)} ghost color="secondary">
              <Trans>Anuluj</Trans>
            </Button>
            <Button onClick={() => handleDelete()} color="danger">
              <Trans>Usuń</Trans>
            </Button>
          </div>
        </Modal>
      )}
      {openModal && renderModalContent()}
    </>
  );
};

export default AdditionalActionCell;
